<template>
    <v-container>
        <v-card class="pa-16">
            <v-row>
                <v-col cols="12" md="6">
                    <v-card-title class="text-h5">Quotation / Fee Estimate</v-card-title>
                </v-col>
                <v-col align="right" cols="12" md="6">
                    <v-btn to="/add-quotation" color="#0A7EA5" rounded dark class="mb-2">
                        <span>Add</span>
                        <v-icon small right>add</v-icon>
                    </v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="8">
                    <v-text-field v-model="searchInput" append-icon="mdi-magnify" label="Search (Doc No. / Client)"
                        single-line hide-details outlined dense clearable @keyup.enter="applySearch"
                        @click:append="applySearch" @click:clear="clearSearch"></v-text-field>
                    <h4 class="red--text">* input text and enter for search</h4>
                </v-col>
                <v-col v-if="isAdmin" cols="4">
                    <v-select v-model="filterDepartment" :items="departmentOptions" label="แผนก" outlined dense
                        clearable></v-select>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="3">
                    <v-select v-model="filterDocType" :items="docTypeOptions" label="ประเภทเอกสาร" outlined dense
                        clearable></v-select>
                </v-col>
                <v-col cols="12" md="3">
                    <v-select v-model="filterStatus" :items="statusOptions" label="สถานะ" outlined dense
                        clearable></v-select>
                </v-col>

                <v-col cols="12" md="3">
                    <v-menu v-model="menuFrom" :close-on-content-click="false" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="dateFrom" label="Date From" append-icon="mdi-calendar" readonly
                                outlined dense clearable v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="dateFrom" @input="menuFrom = false"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                    <v-menu v-model="menuTo" :close-on-content-click="false" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="dateTo" label="Date To" append-icon="mdi-calendar" readonly outlined
                                dense clearable v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="dateTo" @input="menuTo = false"></v-date-picker>
                    </v-menu>
                </v-col>
            </v-row>

            <br />

            <v-card outlined tile>
                <v-data-table dense :headers="headers" :items="filteredItems" :loading="isLoadingTable"
                    class="elevation-1">
                    <template v-slot:[`item.doc_type`]="{ item }">
                        <v-chip x-small :color="item.doc_type === 'fixed' ? 'blue' : 'purple'" dark>
                            {{ item.doc_type === "fixed" ? "Quotation" : "Fee Estimate" }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.grand_total_show`]="{ item }">
                        <span v-if="item.doc_type === 'fixed'">{{ fmt(item.grand_total) }}</span>
                        <span v-else class="grey--text font-italic">ตามอัตราในเอกสาร</span>
                    </template>

                    <template v-slot:[`item.status`]="{ item }">
                        <v-chip x-small :color="statusColor(item.status)" dark>
                            {{ statusIcon(item.status) }} {{ statusLabel(item.status) }}
                        </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn v-if="item.status === 'Draft' || item.status === 'Submitted'" icon small color="#1565C0"
                            @click="onEdit(item)" title="แก้ไข">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn v-else icon small color="#1565C0" @click="onView(item)" title="ดูเอกสาร">
                            <v-icon small>mdi-eye</v-icon>
                        </v-btn>

                        <v-btn v-if="canDelete(item)" icon small color="error" @click="onDelete(item)" title="ลบ">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card>
        </v-card>

        <v-dialog v-model="dialogDelete" max-width="420" persistent>
            <v-card class="delete-dialog-card">
                <div class="delete-dialog-icon-wrap">
                    <v-avatar size="64" color="#FDECEA">
                        <v-icon size="34" color="#E53935">mdi-trash-can-outline</v-icon>
                    </v-avatar>
                </div>

                <v-card-text class="text-center pb-0">
                    <div class="text-h6 font-weight-bold black--text mb-1">ยืนยันการลบเอกสาร</div>
                    <v-chip label color="#F5F5F5" class="font-weight-medium black--text mb-3" small>
                        {{ deleteTarget && deleteTarget.doc_no }}
                    </v-chip>
                    <div class="grey--text text--darken-1" style="font-size: 13.5px; line-height: 1.5;">
                        เอกสารนี้จะถูกลบออกจากระบบทันที และ<b>ไม่สามารถกู้คืนได้</b>
                    </div>
                </v-card-text>

                <v-card-actions class="px-6 pb-6 pt-4">
                    <v-row no-gutters>
                        <v-col cols="6" class="pr-2">
                            <v-btn outlined block color="grey darken-1" @click="dialogDelete = false">
                                ยกเลิก
                            </v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-2">
                            <v-btn block color="#E53935" dark depressed @click="confirmDelete">
                                <v-icon left small>mdi-trash-can-outline</v-icon>
                                ลบเอกสาร
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import Swal from "sweetalert2";

export default {
    name: "QuotationList",
    data() {
        return {
            myDepartment: null,

            searchInput: "",
            search: "",
            filterDocType: null,
            filterStatus: null,
            filterDepartment: null,

            dateFrom: this.getFirstDayOfMonth(),
            dateTo: this.getToday(),
            menuFrom: false,
            menuTo: false,

            dialogDelete: false,
            deleteTarget: null,
            isLoadingTable: false,

            docTypeOptions: [
                { text: "Quotation (Fixed Price)", value: "fixed" },
                { text: "Fee Estimate", value: "estimate" },
            ],
            statusOptions: [
                { text: "Draft", value: "Draft" },
                { text: "Submitted", value: "Submitted" },
                { text: "In Progress", value: "InProgress" },
                { text: "Invoiced", value: "Invoiced" },
            ],

            headers: [
                { text: "Document No.", value: "doc_no", divider: true, width: "150px" },
                { text: "Date", value: "doc_date_show", divider: true, width: "100px" },
                { text: "Client", value: "client_name", divider: true, width: "220px" },
                { text: "Department", value: "department", divider: true, width: "110px" },
                { text: "Doc Type", value: "doc_type", divider: true, width: "130px" },
                { text: "Total Amount", value: "grand_total_show", divider: true, align: "end", width: "150px" },
                { text: "Currency", value: "currency", divider: true, align: "end", width: "90px" },
                { text: "Status", value: "status", divider: true, width: "140px" },
                { text: "Editor", value: "user_create", divider: true, width: "90px" },
                { text: "", value: "actions", divider: true, width: "100px", sortable: false },
            ],

            items: [],
            clientOptions: [],
            departmentList: [],
        };
    },
    async mounted() {
        try {
            await api.checkVersion();

            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);
            this.myDepartment = localStorage.getItem(server.DEPARTMENT_ID);

            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$store.state.global_dialog = true;
                this.setupAlertDialog(
                    true,
                    "Authorize Failed!!!",
                    "Please Logout And Login Again!!!",
                    "text-h5 red--text text-center"
                );
                this.$router.back();
                return;
            }

            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.split("/")[1];

            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];

            if (!permission) {
                this.$router.back();
                return;
            }

            this.authorize_view = permission.smd_view >= 1;
            this.authorize_add = permission.smd_add >= 1;
            this.authorize_edit = permission.smd_edit >= 1;
            this.authorize_del = permission.smd_del >= 1;

            if (!this.authorize_view) {
                this.$router.back();
                return;
            }

            if (this.userId && api.isLoggedIn()) {
                // already logged in
            } else {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
                return;
            }
        } catch (error) {
            console.error("QuotationList mounted() auth error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
            return;
        }

        // ===== โหลดข้อมูลจริง แยก catch ออกจาก auth =====
        this.$showLoader();
        try {
            await this.loadCustomer();
            await this.loadDepartment();

            // ถ้าไม่ใช่ admin ล็อค filterDepartment ให้เป็นแผนกตัวเองตั้งแต่แรก
            if (!this.isAdmin) {
                this.filterDepartment = this.myDepartment;
            }

            await this.loadQuotations();
        } catch (error) {
            console.error("QuotationList mounted() load data error:", error);
            await Swal.fire({
                icon: "error",
                title: "เกิดข้อผิดพลาด",
                text: "ไม่สามารถโหลดข้อมูลรายการได้ กรุณาลองใหม่อีกครั้ง",
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
        } finally {
            this.$hideLoader();
        }
    },
    computed: {
        isAdmin() {
            return localStorage.getItem(server.ROLE) === "ADMIN"; // เดิมถูกอยู่แล้ว ใช้ === เทียบตรงๆ ได้เลย
        },
        // ✅ กลับไปแบบเดิม — derive จาก items ที่มีอยู่จริง ไม่ดึงทุกแผนกของบริษัท
        departmentOptions() {
            const depts = this.items.map((d) => d.department).filter(Boolean);
            return [...new Set(depts)].sort();
        },
        filteredItems() {
            return this.items;
        },
    },
    watch: {
        // ✅ ทุก filter เปลี่ยน → ยิง API ใหม่
        filterDocType() {
            this.loadQuotations();
        },
        filterStatus() {
            this.loadQuotations();
        },
        filterDepartment() {
            this.loadQuotations();
        },
        dateFrom() {
            this.loadQuotations();
        },
        dateTo() {
            this.loadQuotations();
        },
    },
    async beforeCreate() {
        this.$store.state.navMenu = true;
    },
    methods: {
        getFirstDayOfMonth() {
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            return `${yyyy}-${mm}-01`;
        },
        getToday() {
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
        },
        async loadCustomer() {
            const result = await api.getAllCustomerListactiveCompany({
                params: {
                    company_id: localStorage.getItem(server.COMPANYID),
                },
            });
            this.clientOptions = result.data.map((item) => ({
                id: item.id,
                name: item.name,
            }));
        },
        async loadDepartment() {
            const res = await api.getAllDepartment_By_CompanyID(
                Number(localStorage.getItem(server.COMPANYID))
            );

            this.departmentList = res.data.map((d) => ({
                id: d.id,
                name: d.department_name,
            }));
        },
        // ✅ หา department_id จากชื่อที่เลือกใน dropdown (dropdown เก็บเป็นชื่อ ไม่ใช่ id)
        getDepartmentIdByName(name) {
            const match = this.departmentList.find((d) => d.name === name);
            return match ? match.id : null;
        },
        async loadQuotations() {
            this.isLoadingTable = true;
            try {
                const params = {
                    company_id: Number(localStorage.getItem(server.COMPANYID)),
                    role: localStorage.getItem(server.ROLE) || "",
                };

                if (this.filterDocType) params.doc_type = this.filterDocType;
                if (this.filterStatus) params.status = this.filterStatus;
                if (this.dateFrom) params.dateFrom = this.dateFrom;
                if (this.dateTo) params.dateTo = this.dateTo;
                if (this.search) params.keyword = this.search;

                if (!this.isAdmin) {
                    params.department_id = this.myDepartment;
                } else if (this.filterDepartment) {
                    const deptId = this.getDepartmentIdByName(this.filterDepartment);
                    if (deptId) params.department_id = deptId;
                }

                const res = await api.billing_expense_getAllQuotations(params);
                const list = Array.isArray(res.data) ? res.data : [];

                this.items = list.map((record) => ({
                    id: record.quotation_id,
                    doc_no: record.doc_no,
                    doc_date: record.doc_date,
                    doc_date_show: this.formatDocDateShow(record.doc_date),
                    client_name: record.client ? record.client.name : `Client #${record.client_id}`,
                    department: record.department ? record.department.name : "-",
                    department_id: record.department_id,
                    doc_type: record.doc_type,
                    grand_total: record.grand_total,
                    currency: record.currency,
                    status: record.status,
                    user_create: record.creator ? this.getUserDisplayName(record.creator) : `User #${record.user_create}`,
                }));
            } catch (error) {
                console.error("loadQuotations error:", error);
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: "ไม่สามารถโหลดข้อมูลรายการได้",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            } finally {
                this.isLoadingTable = false;
            }
        },
        getUserDisplayName(u) {
            const enFirst = (u.firstname_e_n || "").trim();
            const enLast = (u.lastname_e_n || "").trim();

            if (enFirst || enLast) {
                return `${enFirst} ${enLast}`.trim();
            }

            const thFirst = (u.firstname || "").trim();
            const thLast = (u.lastname || "").trim();
            return `${thFirst} ${thLast}`.trim();
        },
        formatDocDateShow(dateOnly) {
            if (!dateOnly) return "";
            const [yyyy, mm, dd] = dateOnly.split("-");
            if (!yyyy || !mm || !dd) return "";
            return `${dd}/${mm}/${yyyy}`;
        },
        applySearch() {
            this.search = this.searchInput;
            this.loadQuotations(); // ✅ ยิง API ใหม่ตอนกด enter/search
        },
        clearSearch() {
            this.searchInput = "";
            this.search = "";
            this.loadQuotations(); // ✅ ยิง API ใหม่ตอน clear
        },
        fmt(n) {
            if (n === null || n === undefined) return "-";
            return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        statusColor(status) {
            return { Draft: "grey", Submitted: "blue", Invoiced: "green", InProgress: "purple" }[status] || "grey";
        },
        statusIcon(status) {
            return status === "Draft" ? "✏️" : status === "Invoiced" || status === "InProgress" ? "🔒" : "";
        },
        statusLabel(status) {
            return { Draft: "Draft", Submitted: "Submitted", Invoiced: "Invoiced", InProgress: "In Progress" }[status];
        },
        canDelete(item) {
            return item.status === "Draft";
        },
        onEdit(item) {
            this.$router.push(`/edit-quotation/${item.id}`);
        },
        onView(item) {
            this.$router.push(`/view-quotation/${item.id}`);
        },
        onDelete(item) {
            this.deleteTarget = item;
            this.dialogDelete = true;
        },
        async confirmDelete() {
            const id = this.deleteTarget.id;
            this.dialogDelete = false;
            this.$showLoader();

            try {
                const res = await api.billing_expense_updateQuotations(id, {
                    is_deleted: true, modeUpdate: "deleteData"
                });

                if (res.status == 200) {
                    await this.loadQuotations();
                    this.$hideLoader();
                    await Swal.fire({
                        icon: "success",
                        title: "ลบสำเร็จ",
                        text: "ลบใบเสนอราคาเรียบร้อยแล้ว",
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                }
            } catch (error) {
                console.error("confirmDelete error:", error);
                this.$hideLoader();
                await Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: error.response?.data?.message || "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            } finally {
                this.deleteTarget = null;
            }
        },
    },
};
</script>

<style scoped>
.delete-dialog-card {
    border-radius: 16px !important;
    padding-top: 28px;
    overflow: visible;
}

.delete-dialog-icon-wrap {
    display: flex;
    justify-content: center;
    margin-top: -8px;
    margin-bottom: 4px;
}
</style>